import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import {
  Container,
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  TextField,
  styled,
  Button,
  AppBar,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  Snackbar,
  SnackbarCloseReason,
  SnackbarContent,
  CircularProgress,
  Fab,
  Alert as MuiAlert
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";

import "./ResultsPage.css"; // Import the CSS file
import { useNavigate } from "react-router-dom";
import { tokens } from "../theme";
import AgentPanel from "./AgentPanel";

interface Entry {
  Name: string;
  Ts: string;
  Text: string;
  EntryType: string;
  SourceName: string;
  Id: string;
InternalEntryId: string;
}

interface Alert {
    EntryId : string;
    Message : string;
    Severity : string;
}

interface JobSource {
  name: string;
  status: string;
  error?: string;
}

interface Warning {
  source: string;
  message: string;
}

const ResultsPage: React.FC = () => {
  const history = useNavigate();
  const { inputText } = useParams<{ inputText: string }>();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [filterText, setFilterText] = useState("");
  const [loading, setLoading] = useState(true);
  const [searchInText, setSearchInText] = useState(false);
  const [expandedPanels, setExpandedPanels] = useState<Set<string>>(new Set());
  const [hiddenEntries, setHiddenEntries] = useState<Set<string>>(new Set());
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [txTs, setTxTs] = useState("");
  const [txContactId, setTxContactId] = useState("");
  const [entries, setEntries] = useState<Entry[]>([]);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [sources, setSources] = useState<JobSource[]>([]);
  const [warnings, setWarnings] = useState<Warning[]>([]);
  const [agentOpen, setAgentOpen] = useState(false);

  const handleBackClick = () => {
    history("/");
  };

  const handleFilterTextChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFilterText(event.target.value);
  };

  const handleSearchInTextChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSearchInText(event.target.checked);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const showHideEntries = (keyword: string) => {
    setHiddenEntries((prev) => {
      const newHiddenEntries = new Set(prev);
      entries.forEach((entry) => {
        const headerText = `[${formatDateTime(
          entry.Ts
        ).toLowerCase()}] [${entry.EntryType.toLowerCase()}] 
      [${entry.SourceName.toLowerCase()}] [${entry.Id.toLowerCase()}] [${entry.Name.toLowerCase()}]`;

        if (
          hiddenEntries.has(headerText) &&
          headerText.includes(keyword.toLowerCase())
        ) {
          newHiddenEntries.delete(headerText);
        } else if (headerText.includes(keyword.toLowerCase())) {
          newHiddenEntries.add(headerText);
        }
      });
      return newHiddenEntries;
    });
    handleMenuClose();
  };

  const filterEntries = (entries: Entry[]) => {
    return entries.filter((entry) => {
      const headerText = `[${formatDateTime(
        entry.Ts
      ).toLowerCase()}] [${entry.EntryType.toLowerCase()}] 
      [${entry.SourceName.toLowerCase()}] [${entry.Id.toLowerCase()}] [${entry.Name.toLowerCase()}]`;
      const collapsibleText = entry.Text.toLowerCase();
      const textMatches = searchInText
        ? headerText.includes(filterText.toLowerCase()) ||
          collapsibleText.includes(filterText.toLowerCase())
        : headerText.includes(filterText.toLowerCase());

      return textMatches && !hiddenEntries.has(headerText);
    });
  };

  const getEntryAccent = (headerText: string) => {
    const t = headerText.toLowerCase();
    if (t.includes('[mysql]')) return tokens.accent;
    if (t.includes('[bigquery]')) return tokens.warning;
    if (t.includes('[logs]')) return tokens.stone;
    return tokens.hairlineStrong;
  };

  const CustomAccordion = styled(Accordion)(() => ({
    backgroundColor: tokens.canvas,
    boxShadow: 'none',
    border: 'none',
    borderBottom: `1px solid ${tokens.hairline}`,
    '&:before': { display: 'none' },
    '&.Mui-expanded': { margin: 0 },
  }));

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let restartsLeft = 1;

    setLoading(true);
    setEntries([]);
    setAlerts([]);
    setWarnings([]);
    setSources([]);

    const handleAxiosError = (error: any) => {
      if (error.response?.status === 401) {
        history("/login");
        return;
      }
      const message = error.response?.data?.error ?? error.message;
      setSnackbarMessage(message);
      setSnackbarOpen(true);
      setLoading(false);
    };

    const poll = async (jobId: string) => {
      if (cancelled) return;
      try {
        const response = await axios.get(`https://debug.slace.com/jobs/${jobId}`);
        if (cancelled) return;

        const job = response.data;
        setSources(job.sources ?? []);

        if (job.status === "done") {
          setEntries(job.result.entries ?? []);
          setAlerts(job.result.alerts ?? []);
          setWarnings(job.result.warnings ?? []);
          setTxTs(job.result.tx_timestamp);
          setTxContactId(job.result.tx_contact_id);
          setLoading(false);
        } else if (job.status === "failed") {
          setSnackbarMessage(job.error);
          setSnackbarOpen(true);
          setLoading(false);
        } else {
          timer = setTimeout(() => poll(jobId), 2000);
        }
      } catch (error: any) {
        if (cancelled) return;
        if (error.response?.status === 404 && restartsLeft > 0) {
          // job expired (e.g. pod restart) — start over, at most once
          restartsLeft--;
          start();
          return;
        }
        handleAxiosError(error);
      }
    };

    const start = async () => {
      try {
        const response = await axios.post(
          `https://debug.slace.com/transactions/${inputText}/jobs`
        );
        if (cancelled) return;
        poll(response.data.job_id);
      } catch (error: any) {
        if (cancelled) return;
        handleAxiosError(error);
      }
    };

    start();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [inputText]);

  const fetchNextTx = async () => {
    try {
      const response = await axios.get(
        `https://debug.slace.com/contacts/${txContactId}/next-tx?ts=${txTs}`
      );

      history("/results/" + response.data.tx_id);
    } catch (error: any) {
      setLoading(false)
      if (error.response) {
        if (error.response.status === 500 || error.response.status === 400) {
          console.log(error.response.data);
          console.log(error.response.data.error);
          setSnackbarMessage(error.response.data.error);
          setSnackbarOpen(true);
          
        } else {
          console.log("Error:", error.response.data);
        }
      } else if (error.request) {
        console.log("No response received:", error.request);
      } else {
        console.log("Error", error.message);
      }
    }
  }

  const fetchPreviousTx = async () => {
    try {
      const response = await axios.get(
        `https://debug.slace.com/contacts/${txContactId}/previous-tx?ts=${txTs}`
      );

      history("/results/" + response.data.tx_id);
    } catch (error: any) {
      setLoading(false)
      if (error.response) {
        if (error.response.status === 500 || error.response.status === 400) {
          console.log(error.response.data);
          console.log(error.response.data.error);
          setSnackbarMessage(error.response.data.error);
          setSnackbarOpen(true);
        } else {
          console.log("Error:", error.response.data);
        }
      } else if (error.request) {
        console.log("No response received:", error.request);
      } else {
        console.log("Error", error.message);
      }
    }
  }

  const formatDateTime = (ts: string) => {
    const date = new Date(ts);
    return date.toLocaleString("pl-PL");
  };

  function formatAndParseJSON(input: string): string {
    // Parse the initial JSON string
    const parsed = JSON.parse(input);

    // Function to recursively parse nested JSON strings
    const reparseNestedJSON = (obj: any): any => {
      for (const key in obj) {
        if (typeof obj[key] === "string") {
          try {
            const nested = JSON.parse(obj[key]);
            if (typeof nested === "object" && nested !== null) {
              obj[key] = reparseNestedJSON(nested);
            }
          } catch (e) {
            // Ignore, not a nested JSON string
          }
        }
      }
      return obj;
    };

    // Reparse nested JSON strings in the parsed object
    const reparsedObject = reparseNestedJSON(parsed);

    // Convert the object back to a formatted JSON string
    const formattedJSON = JSON.stringify(reparsedObject, null, 2);

    return formattedJSON;
  }

  const renderTextContent = (text: string) => {
    try {
      const json = JSON.parse(text);
      return (
        <pre className="pre-wrap">
          {formatAndParseJSON(JSON.stringify(json, null, 2))}
        </pre>
      );
    } catch {
      return <Typography>{text}</Typography>;
    }
  };

  const handleAutoCollapse = () => {
    setExpandedPanels(new Set());
  };

  const handleSnackbarClose = (
    event: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleExport = () => {
    const filename = `tx-${inputText}.log`;
    const fileContent = filterEntries(entries).map(entry => `[${formatDateTime(entry.Ts)}] [${
        entry.EntryType
      }] [${entry.SourceName}] [${entry.Id}] [${entry.Name}]\n\n${entry.Text}`).join('\n\n');
    const blob = new Blob([fileContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedPanels((prev) => {
        const newExpandedPanels = new Set(prev);
        if (isExpanded) {
          newExpandedPanels.add(panel);
        } else {
          newExpandedPanels.delete(panel);
        }
        return newExpandedPanels;
      });
    };
  return (
    <>
      <AppBar position="static">
        <Toolbar style={{ width: "100vw" }}>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={handleMenuClick}
          >
            <MenuIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={handleAutoCollapse}>Auto-Collapse</MenuItem>
            <MenuItem
              onClick={() => {
                console.log(hiddenEntries);
                showHideEntries("[MySQL]");
              }}
            >
              Show/Hide [MySQL]
            </MenuItem>
            <MenuItem onClick={() => showHideEntries("[BigQuery]")}>
              Show/Hide [BigQuery]
            </MenuItem>
            <MenuItem onClick={() => showHideEntries("[HTTP]")}>
              Show/Hide [HTTP]
            </MenuItem>
            <MenuItem onClick={() => showHideEntries("[Logs]")}>
              Show/Hide [Logs]
            </MenuItem>
            <MenuItem onClick={() => showHideEntries("[Other errors]")}>
              Show/Hide [Other errors]
            </MenuItem>
            <MenuItem onClick={handleExport}>Export as file</MenuItem>
          </Menu>
          <Typography sx={{ fontWeight: 700 }}>[ DEBUG TOOL ]</Typography>
          <Button
            onClick={() => setAgentOpen((v) => !v)}
            sx={{ marginLeft: "auto", color: tokens.ink, border: `1px solid ${tokens.hairlineStrong}`, borderRadius: "4px", padding: "0 12px" }}
          >
            [ AI ]
          </Button>
        </Toolbar>
      </AppBar>
      {/* <Box
        display="flex"
        justifyContent="space-between"
        mb={4}
        mt={4}
        ml={4}
        mr={4}
      >
        {txContactId && txTs && (
          <Button

            onClick={() => {
              setTxContactId("");
              setTxTs("");
              setLoading(true);
              setEntries([]);
              setAlerts([]);
              fetchPreviousTx();
            }}
            variant="contained"
            color="primary"
          >
            Previous Transaction
          </Button>
        )}

        {txContactId && txTs && (
          <Button
            onClick={() => {
              setTxContactId("");
              setTxTs("");
              setLoading(true);
              setEntries([]);
              setAlerts([]);
              fetchNextTx();
            }}
            variant="contained"
            color="primary"
          >
            Next Transaction
          </Button>
        )}
      </Box> */}
      <Container sx={{ marginRight: agentOpen ? "436px" : undefined, transition: "margin-right 0.2s" }}>
        <Box mt={2} mb={2}>
          <Typography color="lightgray" variant="h5">
            Transaction: {inputText}
          </Typography>
        </Box>

        <Box mt={2} mb={2}>
          <TextField
            label="Search timeline"
            variant="outlined"
            value={filterText}
            onChange={handleFilterTextChange}
            fullWidth
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={searchInText}
                onChange={handleSearchInTextChange}
                name="searchInText"
                color="primary"
              />
            }
            label="Include payload"
          />
        </Box>
        {loading ? (
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            minHeight="50vh"
          >
            <CircularProgress />
            <Typography variant="h6" color="textSecondary" mb={2}>
              Collecting data...
            </Typography>
            <Box>
              {sources.map((source) => (
                <Box key={source.name} display="flex" alignItems="center" gap={1} mb={0.5}>
                  <Typography
                    component="span"
                    sx={{
                      color:
                        source.status === "done"
                          ? tokens.success
                          : source.status === "failed"
                          ? tokens.danger
                          : tokens.ash,
                      width: 32,
                      flexShrink: 0,
                    }}
                  >
                    {source.status === "done" ? "[x]" : source.status === "failed" ? "[!]" : "[ ]"}
                  </Typography>
                  <Typography color="textSecondary">{source.name}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        ) : (
          <>
            {warnings.length > 0 && (
              <MuiAlert
                severity="warning"
                icon={false}
                sx={{
                  mb: 2,
                  backgroundColor: tokens.canvas,
                  color: tokens.ink,
                  border: `1px solid ${tokens.warning}`,
                  borderRadius: 0,
                }}
              >
                {warnings.map((w, i) => (
                  <div key={i}>
                    [!] [{w.source}] {w.message}
                  </div>
                ))}
              </MuiAlert>
            )}
            {filterEntries(entries).length > 0 ? (
              filterEntries(entries).map((entry, index) => {
                const headerText = `[${formatDateTime(entry.Ts)}] [${
                  entry.EntryType
                }] [${entry.SourceName}] [${entry.Id}] [${entry.Name}]`;
                const accent = getEntryAccent(headerText);
                const alert = alerts ? alerts.find(alert => alert.EntryId === entry.InternalEntryId) : null;
                return (
                  <CustomAccordion
                    key={index}
                    expanded={expandedPanels.has(`panel${index}`)}
                    onChange={handleChange(`panel${index}`)}
                    style={{ borderLeft: `2px solid ${accent}` }}
                  >
                    <AccordionSummary
                      aria-controls={`panel${index}-content`}
                      id={`panel${index}-header`}
                    >
                          <Box display="flex" alignItems="center" width="100%">
                      <Typography sx={{ color: tokens.mute, mr: 1, flexShrink: 0 }}>
                        {expandedPanels.has(`panel${index}`) ? '[-]' : '[+]'}
                      </Typography>
                      <Typography>{headerText}</Typography>
                      {alert && (
                        <MuiAlert
                          icon={false}
                          variant="outlined"
                          severity={alert.Severity as 'info' | 'warning' | 'error'}
                          sx={{ borderRadius: 0, marginLeft: 'auto', marginRight: '20px' }}
                        >
                          {alert.Message}
                        </MuiAlert>
                      )}
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      {renderTextContent(entry.Text)}
                    </AccordionDetails>
                  </CustomAccordion>
                );
              })
            ) : (
              <Typography>
                <p style={{ color: "red" }}>
                  Failed to collect data ({snackbarMessage})
                </p>
              </Typography>
            )}
          </>
        )}

        <Snackbar
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
        >
          <SnackbarContent
            style={{
              backgroundColor: tokens.canvas,
              color: tokens.danger,
              border: `1px solid ${tokens.danger}`,
              borderRadius: 4,
              boxShadow: 'none',
            }}
            message={snackbarMessage}
            action={
              <React.Fragment>
                <IconButton
                  size="small"
                  aria-label="close"
                  color="inherit"
                  onClick={handleSnackbarClose}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </React.Fragment>
            }
          />
        </Snackbar>
      </Container>
      <Fab
        aria-label="back"
        onClick={handleBackClick}
        style={{
          position: "fixed",
          bottom: "50px",
          left: "50px",
          backgroundColor: tokens.ink,
          color: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <ArrowBackIcon />
      </Fab>
      <AgentPanel txId={inputText ?? ""} open={agentOpen} onClose={() => setAgentOpen(false)} />
    </>
  );
};

export default ResultsPage;
